import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { ReactComponent as AvatarIcon } from "../../../assets/icons/logo-88.svg";
import AppLogo from "../../ui/AppLogo";
import { NavItem } from "./navLinkData";

interface SidebarProps {
  allowedNavItems: NavItem[];
  isExpanded: boolean;
  isMobile?: boolean;
  onMobileClose?: () => void;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  activeNav: string;
  activeSubNav: string | null;
  openDropdowns: Set<string>;
  handleSubNavClick: (subNavId: string) => void;
  handleNavClick: (navId: string, hasSubItems: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  allowedNavItems,
  isExpanded,
  isMobile = false,
  onMobileClose,
  setIsExpanded,
  activeNav,
  activeSubNav,
  openDropdowns,
  handleSubNavClick,
  handleNavClick,
}) => {
  return (
    <motion.aside
      initial={false}
      animate={{ width: isExpanded ? 256 : 76 }}
      transition={{ duration: 0.25, ease: "easeInOut" }}
      className="relative mx-auto flex h-full max-w-[85vw] flex-col border-r border-slate-200 bg-white"
    >
      {isMobile && (
        <button
          type="button"
          onClick={onMobileClose}
          aria-label="Close navigation menu"
          className="absolute right-3 top-4 z-10 rounded-full p-2 text-slate-500 hover:bg-slate-100"
        >
          <X size={20} />
        </button>
      )}

      {/* Logo */}
      <div
        className={`flex h-[72px] shrink-0 items-center border-b border-slate-100 ${
          isExpanded ? "px-5" : "justify-center px-2"
        }`}
      >
        {isExpanded ? (
          <AppLogo
            className="h-[41px] w-[216px] object-cover"
            style={{ objectPosition: "center 48%" }}
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <AvatarIcon className="h-6 w-6" />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {allowedNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeNav === item.id;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isDropdownOpen = openDropdowns.has(item.id);

            return (
              <li key={item.id}>
                <button
                  type="button"
                  title={isExpanded ? undefined : item.name}
                  onClick={() => handleNavClick(item.id, hasSubItems || false)}
                  className={`group relative flex w-full items-center rounded-xl py-2.5 transition-colors duration-150 ${
                    isExpanded ? "justify-between px-3" : "justify-center px-0"
                  } ${
                    isActive
                      ? "bg-primary/10 font-semibold text-primary"
                      : "font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-primary" />
                  )}
                  <span className="flex min-w-0 items-center gap-3">
                    <Icon
                      className={`h-[22px] w-[22px] shrink-0 ${
                        isActive
                          ? "text-primary"
                          : "text-slate-400 group-hover:text-slate-700"
                      }`}
                    />
                    {isExpanded && (
                      <span className="truncate text-[14px]">{item.name}</span>
                    )}
                  </span>
                  {isExpanded && hasSubItems && (
                    <motion.span
                      animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-slate-400"
                    >
                      <ChevronDown size={16} />
                    </motion.span>
                  )}
                </button>

                {/* Sub Navigation */}
                <AnimatePresence initial={false}>
                  {isExpanded && hasSubItems && isDropdownOpen && (
                    <motion.ul
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="ml-[22px] mt-1 space-y-0.5 overflow-hidden border-l border-slate-200 pl-3"
                    >
                      {item.subItems?.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = activeSubNav === subItem.id;

                        return (
                          <li key={subItem.id}>
                            <button
                              type="button"
                              onClick={() => handleSubNavClick(subItem.id)}
                              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors duration-150 ${
                                isSubActive
                                  ? "bg-primary/10 font-semibold text-primary"
                                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                              }`}
                            >
                              {SubIcon && <SubIcon size={16} />}
                              <span className="truncate">{subItem.name}</span>
                            </button>
                          </li>
                        );
                      })}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        className={`${
          isMobile ? "hidden" : "flex"
        } absolute -right-3 top-[60px] h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-primary hover:text-primary`}
      >
        {isExpanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
      </button>
    </motion.aside>
  );
};

export default Sidebar;
